// src/views/ConstructionConsultation.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Mail, Award, MapPin, Briefcase, ChevronLeft, ChevronRight, Search, Loader2 } from "lucide-react";
import useAddressStore from "../store/useAddressStore";
import useConsultantSubscriptionStore from "../store/consultant/useConsultantSubscriptionStore";
import Navbarlanding from "../components/Navbarlanding";
import MobileBottomNav from "../components/common/MobileBottomNav";
import toast from "react-hot-toast";

const ConsultantDiscoveryCard = ({ consultant }) => {
  const [isFlipped, setIsFlipped] = useState(false);

  const handleCall = () => {
    if (consultant.phone_number) {
      window.location.href = `tel:${consultant.phone_number}`;
    } else {
      toast.error("Phone number not available");
    }
  };

  const handleEmail = () => {
    if (consultant.email) {
      window.location.href = `mailto:${consultant.email}`;
    } else {
      toast.error("Email not available");
    }
  };

  return (
    <div
      className="relative w-full h-[140px] cursor-pointer perspective-1000 group lg:h-[160px]"
      onClick={() => setIsFlipped(!isFlipped)}
    >
      <motion.div
        className="relative w-full h-full"
        initial={false}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.6, type: "spring", stiffness: 260, damping: 20 }}
        style={{ transformStyle: "preserve-3d" }}
      >
        {/* Front Side - Sketch/Builder Card style */}
        <div
          className="absolute w-full h-full backface-hidden shadow-sm rounded-xl overflow-hidden border border-gray-200 bg-white p-3 md:p-4"
          style={{ backfaceVisibility: "hidden" }}
        >
          <div className="h-full flex items-start gap-4">
            {/* Profile Side */}
            <div className="relative flex-shrink-0">
              <img
                src={consultant.profile_image || "/assets/Support.png"}
                alt={consultant.name}
                className="w-16 h-16 rounded-lg border border-gray-100 shadow-sm object-cover md:w-20 md:h-20"
                onError={(e) => { e.target.src = "/assets/Support.png"; }}
              />
              <div className="absolute -bottom-1 -right-1 bg-yellow-400 text-black p-0.5 rounded-full border border-white">
                <Award size={10} />
              </div>
            </div>

            {/* Info Side - Based on Sketch */}
            <div className="flex-grow flex flex-col justify-between h-full min-w-0">
              <div className="space-y-0.5">
                <h3 className="text-sm font-bold text-gray-900 truncate md:text-base">
                  {consultant.name} <span className="text-blue-600 font-medium">[{consultant.qualification || "Consultant"}]</span>
                </h3>
                <p className="text-[10px] text-blue-600 font-bold uppercase tracking-wider mb-1">
                  Ducktail ID: {consultant.consultant_id}
                </p>

                <div className="space-y-0.5">
                  <div className="flex items-center gap-1.5 text-gray-600">
                    <Briefcase size={12} className="text-yellow-500 flex-shrink-0" />
                    <span className="text-[11px] font-medium truncate">Experience: {consultant.experience || "0"}+ Years</span>
                  </div>
                  <div className="flex items-start gap-1.5 text-gray-600">
                    <MapPin size={12} className="text-yellow-500 flex-shrink-0 mt-0.5" />
                    <span className="text-[11px] font-medium line-clamp-2">
                      {consultant.village_name || "Village"}, {consultant.taluk}, {consultant.district} - {consultant.pincode || ""}
                    </span>
                  </div>
                </div>
              </div>

              {/* Bottom Action Indicator */}
              <div className="flex justify-end items-center mt-auto pt-2 border-t border-gray-50">
                <div className="flex items-center gap-1">
                  <span className="text-[9px] font-bold text-gray-400 uppercase">View Contact</span>
                  <ChevronRight size={10} className="text-yellow-400" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Back Side - Action side */}
        <div
          className="absolute w-full h-full backface-hidden shadow-lg rounded-xl overflow-hidden"
          style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
        >
          <div className="h-full bg-gray-900 p-4 flex flex-col justify-center gap-3 relative">
            <div className="text-center">
              <p className="text-[9px] font-bold text-yellow-400 uppercase tracking-widest">Connect with</p>
              <h4 className="text-white text-sm font-bold truncate">{consultant.name}</h4>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); handleCall(); }}
                className="flex flex-col items-center justify-center gap-1 bg-yellow-400 hover:bg-yellow-500 text-black py-2.5 rounded-lg font-bold transition-all active:scale-95"
              >
                <Phone size={16} />
                <span className="text-[10px]">CALL</span>
              </button>

              <button
                onClick={(e) => { e.stopPropagation(); handleEmail(); }}
                className="flex flex-col items-center justify-center gap-1 bg-white hover:bg-gray-100 text-black py-2.5 rounded-lg font-bold transition-all active:scale-95"
              >
                <Mail size={16} />
                <span className="text-[10px]">EMAIL</span>
              </button>
            </div>

            <button
              className="text-[9px] text-white hover:text-white transition-colors font-bold uppercase tracking-widest text-center"
              onClick={(e) => { e.stopPropagation(); setIsFlipped(false); }}
            >
              Back to Info
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const ConstructionConsultation = () => {
  const navigate = useNavigate();
  const [view, setView] = useState("search"); // 'search' or 'results'
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedTaluk, setSelectedTaluk] = useState("");

  const {
    districts,
    taluks,
    fetchMappedDistricts,
    fetchMappedTaluks,
    isLoadingDistricts,
    isLoadingTaluks,
  } = useAddressStore();

  const { activeConsultants, isLoading: isLoadingConsultants, fetchActiveConsultants } = useConsultantSubscriptionStore();

  useEffect(() => {
    fetchMappedDistricts("Tamil Nadu");
  }, [fetchMappedDistricts]);

  useEffect(() => {
    if (selectedDistrict) {
      fetchMappedTaluks(selectedDistrict, "Tamil Nadu");
    } else {
      useAddressStore.setState({ taluks: [] });
    }
  }, [selectedDistrict, fetchMappedTaluks]);

  const handleSearch = async () => {
    if (!selectedDistrict || !selectedTaluk) {
      toast.error("Please select both district and taluk");
      return;
    }
    await fetchActiveConsultants(selectedDistrict, selectedTaluk);
    setView("results");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    setView("search");
  };

  const handleTalukChangeInResults = async (taluk) => {
    setSelectedTaluk(taluk);

    if (!selectedDistrict || !taluk) {
      return;
    }

    await fetchActiveConsultants(selectedDistrict, taluk);
    setView("results");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className={`w-full min-h-screen ${view === "search" ? "bg-[#121212]" : "bg-white"} flex flex-col relative`}>
      <style font-display="swap">{`
                .perspective-1000 { perspective: 1000px; }
                .backface-hidden { backface-visibility: hidden; -webkit-backface-visibility: hidden; }
                @keyframes fade-in-down {
                    from { opacity: 0; transform: translateY(-20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fade-in-down { animation: fade-in-down 0.8s ease-out; }
            `}</style>

      {/* Background for Search View (Matching Support.jsx style) */}
      {view === "search" && (
        <div className="fixed inset-0 z-0">
          <img
            src="/assets/Construction Consult.jpg"
            alt="Background"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"></div>
        </div>
      )}

      {/* Navbar with Transparent Mode */}
      <Navbarlanding transparent={view === "search"} transparentLightText />

      <main className="relative z-10 flex-grow flex flex-col overflow-y-auto">
        {view === "search" ? (
          <div className="flex-grow flex flex-col items-center justify-start px-4 py-4 md:py-12 md:pb-32 min-h-full">
            {/* Header Section */}
            <div className="text-center mb-6 md:mb-10 animate-fade-in-down pt-8 sm:pt-0">
              <span className="block text-yellow-400 font-bold tracking-widest uppercase text-xs md:text-sm mb-2 drop-shadow-lg">
                Construction Consultation
              </span>
              <h1 className="text-4xl md:text-6xl font-black text-white mb-4 tracking-tighter drop-shadow-2xl">
                Find Your <span className="text-yellow-400">Expert</span>
              </h1>
              <p className="text-gray-200 text-sm md:text-lg max-w-lg mx-auto leading-relaxed font-medium drop-shadow-md">
                We offer reliable advice and support throughout your project every step of the way in building your dream into reality.
              </p>
            </div>

            {/* Search Card - Integrated (No White BG) */}
            <div className="w-full max-w-lg bg-black/20 backdrop-blur-md border border-white/20 rounded-3xl p-6 md:p-8 shadow-2xl relative group overflow-hidden">
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-gray-300 uppercase tracking-widest ml-1">Select District</label>
                    <div className="relative">
                      <select
                        value={selectedDistrict}
                        onChange={(e) => {
                          setSelectedDistrict(e.target.value);
                          setSelectedTaluk("");
                          setView("search");
                        }}
                        className="w-full bg-white border border-gray-300 text-gray-900 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 appearance-none transition-all cursor-pointer"
                        disabled={isLoadingDistricts}
                      >
                        <option value="">Choose District...</option>
                        {districts?.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-yellow-400">
                        <MapPin size={18} />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-gray-300 uppercase tracking-widest ml-1">Select Taluk</label>
                    <div className="relative">
                      <select
                        value={selectedTaluk}
                        onChange={(e) => setSelectedTaluk(e.target.value)}
                        className="w-full bg-white border border-gray-300 text-gray-900 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 appearance-none transition-all cursor-pointer disabled:opacity-50"
                        disabled={!selectedDistrict || isLoadingTaluks}
                      >
                        <option value="">Choose Taluk...</option>
                        {taluks?.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-yellow-400">
                        <Search size={18} />
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleSearch}
                  disabled={isLoadingConsultants}
                  className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-black py-4 rounded-xl display-flex shadow-lg transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-3 group tracking-widest text-xs"
                >
                  {isLoadingConsultants ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <>
                      SEARCH CONSULTANTS
                      <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="px-4 py-8 md:px-12 max-w-7xl mx-auto w-full pb-24 h-full overflow-y-auto">
            {/* Results Header & Filter */}
            <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div className="flex-grow">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-yellow-400 font-bold text-xs uppercase tracking-widest drop-shadow-md">Active Search Results</span>
                  <div className="h-px bg-gray-200 flex-grow"></div>
                </div>
                <h2 className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight">
                  Consultants in <span className="text-yellow-400">{selectedTaluk}, {selectedDistrict}</span>
                </h2>
              </div>

              {/* In-page Filters */}
              <div className="flex flex-col sm:flex-row gap-3 min-w-[300px]">
                <div className="flex-1">
                  <select
                    value={selectedDistrict}
                    onChange={(e) => {
                      setSelectedDistrict(e.target.value);
                      setSelectedTaluk("");
                    }}
                    className="w-full bg-gray-50 border border-gray-200 text-gray-800 rounded-lg px-3 py-2 text-sm focus:outline-none shadow-sm cursor-pointer"
                  >
                    <option value="">District</option>
                    {districts?.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <select
                    value={selectedTaluk}
                    onChange={(e) => handleTalukChangeInResults(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 text-gray-800 rounded-lg px-3 py-2 text-sm focus:outline-none shadow-sm cursor-pointer"
                  >
                    <option value="">Taluk</option>
                    {taluks?.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* List Section */}
            {isLoadingConsultants ? (
              <div className="flex flex-col items-center justify-center py-24">
                <Loader2 className="w-12 h-12 text-yellow-400 animate-spin mb-4" />
                <p className="text-gray-500 font-bold tracking-widest uppercase text-xs">Fetching Experts...</p>
              </div>
            ) : activeConsultants.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-3xl p-16 text-center shadow-sm">
                <div className="bg-yellow-400/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-yellow-400 border border-yellow-400/20">
                  <Search size={32} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">No Consultants Found</h3>
                <p className="text-gray-500 max-w-xs mx-auto">
                  We couldn't find any active consultants in this specific area. Try searching in a nearby district.
                </p>
                <button
                  onClick={handleBack}
                  className="mt-8 text-yellow-400 font-bold flex items-center gap-2 mx-auto hover:text-yellow-500 transition-colors"
                >
                  <ChevronLeft size={18} /> Change Search Area
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {activeConsultants.map((consultant) => (
                  <ConsultantDiscoveryCard key={consultant.id} consultant={consultant} />
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      <MobileBottomNav />
    </div>
  );
};

export default ConstructionConsultation;